import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { KataSearchViewModelItem } from './KataSearchViewModelItem';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';
import { FolderItemTitle } from '../../items';
import { searchResultsAtom } from '../SearchResult/trpcSearchResultState';
import type { FolderJSON, WorkspaceJSON } from '../SearchResult/trpcSearchResultState';

type FolderItemProps = {
  workspace: WorkspaceJSON;
  folder: FolderJSON;
  searchKeyword: string;
  rootFolderTree: RootFolderTree;
};

export const KataSearchFolderItem: React.FC<FolderItemProps> = ({
  workspace,
  rootFolderTree,
  folder,
  searchKeyword,
}: FolderItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const trpcResults = useAtomValue(searchResultsAtom);

  const toggleFolder = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const sortedFolders = useMemo(() => {
    return rootFolderTree
      .getFolderIdsOf(folder.id)
      .map((fid) => trpcResults.kataResults.folders[fid])
      .filter((folder) => !!folder)
      .sort();
  }, [rootFolderTree, trpcResults, folder.id]);

  const sortedViewModels = useMemo(() => {
    return rootFolderTree
      .getViewModelIdsOf(folder.id)
      .map((viewModelId) => trpcResults.kataResults.viewModels[viewModelId])
      .filter((viewModel) => !!viewModel)
      .sort();
  }, [rootFolderTree, trpcResults, folder.id]);

  useEffect(() => {
    if (searchKeyword) {
      setIsOpen(true);
    }
  }, [searchKeyword]);

  return (
    <div>
      <div className="flex items-center">
        <button className="mr-1" onClick={toggleFolder}>
          <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretRight} className="size-4" />
        </button>
        <div className="grow overflow-hidden rounded-sm hover:bg-gray-300">
          <FolderItemTitle
            workspaceId={workspace.id}
            folderId={folder.id}
            folderName={folder.name}
            searchKeyword={searchKeyword}
            isExternal
          />
        </div>
      </div>

      {isOpen && (
        <div className="ml-4">
          {sortedFolders.map((folder) => (
            <KataSearchFolderItem
              key={folder.id}
              workspace={workspace}
              rootFolderTree={rootFolderTree}
              folder={folder}
              searchKeyword={searchKeyword}
            />
          ))}
          {sortedViewModels.map((viewModel) => (
            <KataSearchViewModelItem key={viewModel.id} viewModel={viewModel} searchKeyword={searchKeyword} />
          ))}
        </div>
      )}
    </div>
  );
};
